"use client";

import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetchJson } from "@/lib/authenticatedFetch";
import { getIncentiveState, verifyIncentiveTask } from "@/lib/api/incentive";
import { getTemplateUrl } from "@/lib/templates/templateUrl";
import type { SearchResponse, SearchResultType } from "@/hooks/useSearch";
import type { CUBalanceResponse, CURunsResponse, CUUsageByWorkflowResponse, CUUsageResponse } from "@/types/cu";
import type { OrganizationInvite, OrganizationMember } from "@/types/organization";
import { defineVoiceTool } from "realtime-voice-component";
import { z } from "zod";
import { compactOrganizationInvite, compactOrganizationMember } from "./voiceCompact";
import { clampLimit, resolveOrganizationInvite, resolveOrganizationMember } from "./voiceEntityResolvers";
import { ensureOrgId, ensurePermission, requireConfirmation } from "./voiceToolRuntime";
import { MAX_TOOL_ITEMS, organizationRoleSchema, type B3OSVoiceToolsOptions } from "./voiceToolTypes";

const PLUGINS = [
  {
    id: "plugin-databases",
    name: "Databases",
    description: "Store and query structured data for workflows with built-in persistence.",
    href: "/databases",
  },
];

type MembersResponse = {
  data: {
    items: OrganizationMember[];
    hasMore: boolean;
  };
};

type InvitesResponse = {
  data: {
    items: OrganizationInvite[];
    hasMore: boolean;
  };
};

type SearchApiResponse = {
  code: number;
  message: string;
  data: SearchResponse;
};

function currentYearMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

async function listOrganizationMembers(orgId: string) {
  const response = await authenticatedFetchJson<MembersResponse>(
    `${V2_ENDPOINT}/v1/organizations/${orgId}/members?limit=100`,
    { orgId },
  );
  return response.data.items;
}

async function listOrganizationInvites(orgId: string) {
  const response = await authenticatedFetchJson<InvitesResponse>(
    `${V2_ENDPOINT}/v1/organizations/${orgId}/invites?limit=100`,
    { orgId },
  );
  return response.data.items;
}

async function searchB3OS(query: string, types: SearchResultType[], limit: number) {
  const params = new URLSearchParams({
    q: query,
    limit: String(clampLimit(limit)),
    offset: "0",
  });
  for (const type of types) {
    params.append("types", type);
  }

  const response = await authenticatedFetchJson<SearchApiResponse>(`${V2_ENDPOINT}/v1/search?${params.toString()}`);
  if (response.code !== 0) {
    throw new Error(response.message || "Search failed.");
  }
  return response.data;
}

function getSearchResultPath(result: SearchResponse["results"][number]) {
  if (result.type === "template") {
    return result.slug ? getTemplateUrl({ id: result.id, slug: result.slug }) : getTemplateUrl({ id: result.id });
  }
  if (result.type === "workflow") return `/workflows/edit?id=${encodeURIComponent(result.id)}`;
  if (result.type === "action" || result.type === "trigger") return "/workflows/edit";
  return null;
}

export function createAdminAnalyticsVoiceTools(options: B3OSVoiceToolsOptions) {
  return [
    defineVoiceTool({
      name: "search_b3os_commands",
      description:
        "Search templates, workflows, actions, and triggers, optionally opening the top result. This is the executable voice equivalent of the command menu search.",
      parameters: z.object({
        query: z.string().min(2),
        types: z.array(z.enum(["template", "action", "trigger", "workflow"])).optional(),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
        openFirst: z.boolean().optional(),
      }),
      execute: async ({ query, types, limit, openFirst }) => {
        const searchTypes = (types ?? ["template", "action", "trigger", "workflow"]) as SearchResultType[];
        const result = await searchB3OS(query, searchTypes, clampLimit(limit));
        const first = result.results[0];
        const path = first ? getSearchResultPath(first) : null;

        if (openFirst && path) {
          options.router.push(path);
        }

        return {
          ok: true,
          query: result.query,
          totalHits: result.totalHits,
          opened: openFirst && path ? { path, result: first } : null,
          results: result.results,
        };
      },
    }),
    defineVoiceTool({
      name: "list_plugins",
      description: "List plugins currently exposed by the B3OS plugins page.",
      parameters: z.object({}),
      execute: () => ({ ok: true, plugins: PLUGINS }),
    }),
    defineVoiceTool({
      name: "plugin_action",
      description: "Open a plugin surface. Plugin installation/configuration stays in the plugin UI.",
      parameters: z.object({
        plugin: z.string().min(1).optional().describe("Plugin id or name. Omit to open the plugins page."),
      }),
      execute: ({ plugin }) => {
        const match = plugin
          ? PLUGINS.find(item => item.id === plugin || item.name.toLowerCase() === plugin.toLowerCase())
          : null;
        const destination = match?.href ?? "/plugins";
        options.router.push(destination);
        return { ok: true, destination, plugin: match };
      },
    }),
    defineVoiceTool({
      name: "get_organization_context",
      description: "Read selected organization members, pending invites, and current voice permission context.",
      parameters: z.object({}),
      execute: async () => {
        const orgId = ensureOrgId(options);
        const [members, invites] = await Promise.all([listOrganizationMembers(orgId), listOrganizationInvites(orgId)]);
        return {
          ok: true,
          organization: {
            id: orgId,
            name: options.selectedOrgName ?? null,
            currentRole: options.currentOrgRole ?? null,
            permissions: options.currentOrgPermissions ?? null,
          },
          members: members.map(compactOrganizationMember),
          pendingInvites: invites
            .filter(invite => invite.status === "pending" || invite.status === "expired")
            .map(compactOrganizationInvite),
        };
      },
    }),
    defineVoiceTool({
      name: "organization_member_action",
      description:
        "Invite members, update roles, remove members, cancel invites, or resend invites. Requires owner permissions and confirmation.",
      parameters: z.object({
        action: z.enum(["invite_email", "update_member_role", "remove_member", "cancel_invite", "resend_invite"]),
        email: z.string().email().optional(),
        member: z.string().optional().describe("Member id, user id, email, or name."),
        invite: z.string().optional().describe("Invite id or email."),
        role: organizationRoleSchema.optional(),
      }),
      execute: async ({ action, email, member, invite, role }) => {
        const orgId = ensureOrgId(options);
        ensurePermission(
          options,
          action === "remove_member"
            ? "canRemoveMembers"
            : action === "update_member_role"
              ? "canUpdateRole"
              : "canInvite",
        );

        if (action === "invite_email") {
          if (!email || !role) return { ok: false, reason: "invite_email requires email and role." };
          return requireConfirmation(options, {
            action,
            description: `Invite ${email} as ${role}`,
            details: { email, role },
            risk: "write",
            title: "Confirm organization invite",
            successMessage: `Invite sent to ${email}`,
            execute: async () => {
              const response = await authenticatedFetchJson<{ data: OrganizationInvite }>(
                `${V2_ENDPOINT}/v1/organizations/${orgId}/invites/email`,
                {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ email, role }),
                  orgId,
                },
              );
              return { ok: true, invite: compactOrganizationInvite(response.data) };
            },
          });
        }

        if (action === "update_member_role") {
          if (!member || !role) return { ok: false, reason: "update_member_role requires member and role." };
          const members = await listOrganizationMembers(orgId);
          const resolved = resolveOrganizationMember(members, member);
          if (!resolved.ok) return resolved;
          return requireConfirmation(options, {
            action,
            description: `Change ${resolved.item.userEmail || resolved.item.userId} to ${role}`,
            details: { member: compactOrganizationMember(resolved.item), role },
            risk: "write",
            title: "Confirm role change",
            successMessage: "Organization role updated",
            execute: async () => {
              const response = await authenticatedFetchJson<{ data: OrganizationMember }>(
                `${V2_ENDPOINT}/v1/organizations/${orgId}/members/${resolved.item.userId}/role`,
                {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ role }),
                  orgId,
                },
              );
              return { ok: true, member: compactOrganizationMember(response.data) };
            },
          });
        }

        if (action === "remove_member") {
          if (!member) return { ok: false, reason: "remove_member requires member." };
          const members = await listOrganizationMembers(orgId);
          const resolved = resolveOrganizationMember(members, member);
          if (!resolved.ok) return resolved;
          return requireConfirmation(options, {
            action,
            description: `Remove ${resolved.item.userEmail || resolved.item.userId} from the organization`,
            details: { member: compactOrganizationMember(resolved.item) },
            risk: "destructive",
            title: "Confirm member removal",
            successMessage: "Organization member removed",
            execute: async () => {
              await authenticatedFetchJson(`${V2_ENDPOINT}/v1/organizations/${orgId}/members/${resolved.item.userId}`, {
                method: "DELETE",
                orgId,
              });
              return { ok: true, member: compactOrganizationMember(resolved.item) };
            },
          });
        }

        if (!invite) return { ok: false, reason: `${action} requires invite.` };
        const invites = await listOrganizationInvites(orgId);
        const resolved = resolveOrganizationInvite(invites, invite);
        if (!resolved.ok) return resolved;
        const method = action === "cancel_invite" ? "DELETE" : "POST";
        const suffix = action === "resend_invite" ? "/resend" : "";

        return requireConfirmation(options, {
          action,
          description:
            action === "cancel_invite"
              ? `Cancel invite for ${resolved.item.inviteeEmail || resolved.item.id}`
              : `Resend invite for ${resolved.item.inviteeEmail || resolved.item.id}`,
          details: { invite: compactOrganizationInvite(resolved.item) },
          risk: action === "cancel_invite" ? "destructive" : "write",
          title: "Confirm invite action",
          successMessage: action === "cancel_invite" ? "Invite canceled" : "Invite resent",
          execute: async () => {
            await authenticatedFetchJson(
              `${V2_ENDPOINT}/v1/organizations/${orgId}/invites/${resolved.item.id}${suffix}`,
              {
                method,
                orgId,
              },
            );
            return { ok: true, invite: compactOrganizationInvite(resolved.item) };
          },
        });
      },
    }),
    defineVoiceTool({
      name: "get_usage_analytics_summary",
      description: "Read CU balance, monthly usage, recent CU-costed runs, and optional workflow usage breakdown.",
      parameters: z.object({
        yearMonth: z
          .string()
          .regex(/^\d{4}-\d{2}$/)
          .optional(),
        from: z.number().int().positive().optional().describe("Unix timestamp for workflow usage breakdown."),
        to: z.number().int().positive().optional().describe("Unix timestamp for workflow usage breakdown."),
        limit: z.number().int().positive().max(MAX_TOOL_ITEMS).optional(),
      }),
      execute: async ({ yearMonth = currentYearMonth(), from, to, limit }) => {
        const orgId = ensureOrgId(options);
        const [balance, usage, runs, byWorkflow] = await Promise.all([
          authenticatedFetchJson<CUBalanceResponse>(`${V2_ENDPOINT}/v1/organizations/${orgId}/cu`, { orgId }),
          authenticatedFetchJson<CUUsageResponse>(
            `${V2_ENDPOINT}/v1/organizations/${orgId}/cu/usage?yearMonth=${yearMonth}`,
            { orgId },
          ),
          authenticatedFetchJson<CURunsResponse>(
            `${V2_ENDPOINT}/v1/organizations/${orgId}/cu/runs?limit=${clampLimit(limit)}`,
            { orgId },
          ),
          from && to
            ? authenticatedFetchJson<CUUsageByWorkflowResponse>(
                `${V2_ENDPOINT}/v1/organizations/${orgId}/cu/usage-by-workflow?from=${from}&to=${to}`,
                { orgId },
              )
            : Promise.resolve(null),
        ]);
        return {
          ok: true,
          organizationId: orgId,
          balance: balance.data,
          usage: usage.data,
          recentRuns: runs.data.items,
          usageByWorkflow: byWorkflow?.data ?? null,
          analyticsRouteAvailable: false,
        };
      },
    }),
    defineVoiceTool({
      name: "get_incentive_summary",
      description: "Read incentive task progress, rewards, and completion totals.",
      parameters: z.object({}),
      execute: async () => {
        const state = await getIncentiveState();
        return { ok: true, state };
      },
    }),
    defineVoiceTool({
      name: "incentive_action",
      description:
        "Open incentives or verify an incentive task. Verification can grant credits and requires confirmation.",
      parameters: z.object({
        action: z.enum(["open", "verify_task"]),
        taskType: z.string().optional(),
      }),
      execute: async ({ action, taskType }) => {
        if (action === "open") {
          options.router.push("/incentive");
          return { ok: true, destination: "/incentive" };
        }

        if (!taskType) return { ok: false, reason: "verify_task requires taskType." };
        return requireConfirmation(options, {
          action,
          description: `Verify incentive task ${taskType}`,
          details: { taskType },
          risk: "financial",
          title: "Confirm incentive verification",
          successMessage: "Incentive verification requested",
          execute: async () => {
            const result = await verifyIncentiveTask(taskType);
            return { ok: true, taskType, result };
          },
        });
      },
    }),
  ];
}
